import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Tooltip } from "primereact/tooltip"

import { Button, TextAreaField, TextInputField } from "../../../helpers"
import Loading from "../../../common/Loading"
import { validateInputs } from "../../../utils"
import { ConversionProbabilityValidationSchema } from "../../../validation/conversionProbabilityValidation"
import "../index.css"

import { raiseToast } from "../../../services/redux/reducers/toast/toast"
import { updateBuyerQuote } from "../../../services/api/buyerQuote"


const ConversionProbabilityDialog = (props) => {
    const { buyerQuote, updateBuyerQuoteState } = props
    const userDetails = useSelector((state) => state.userDetails)
    const companyUsers = userDetails.PMCompanyDetails.companyUsers
    const userId = userDetails.userId
    const dispatch = useDispatch()

    const [conversionProbability, setConversionProbability] = useState(buyerQuote.conversionProbability || '')
    const [conversionRemarks, setConversionRemarks] = useState(buyerQuote.conversionRemarks || '')
    const [isEditing, setIsEditing] = useState(!(buyerQuote.conversionProbability && buyerQuote.conversionRemarks))
    const [updatedBy, setUpdatedBy] = useState('')
    const [updatedAt, setUpdatedAt] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [formErrors, setFormErrors] = useState({});

    const companyRoleId =  userDetails.rolesList.find(each => each.userRoleId === userDetails.activeRoleId)?.companyRoleId
    const canEdit = companyRoleId === 49050 && buyerQuote.bQuotTotalItemAmountBeforeTax > 500000  // respective userRoleName is "CLIENT SUCCESS" 

    let tooltipMessage = null
    if (companyRoleId !== 49050) {
        tooltipMessage = "Only CS Team are allowed to add/update conversion probability."
    } else if (buyerQuote.bQuotTotalItemAmountBeforeTax <= 500000) {
        tooltipMessage = "Price Before Tax should be greater than 5,00,000 to add/update conversion prob."
    }

    useEffect(() => {
        if (buyerQuote.conversionRemarksUpdatedBy) {
            const updatedByUser = companyUsers.find(user => user.userId === buyerQuote.conversionRemarksUpdatedBy)
            if (updatedByUser) {
                setUpdatedBy(`${updatedByUser.firstName} ${updatedByUser.lastName}`)
            }
        } else {
            setUpdatedBy('')
        }
        setUpdatedAt(buyerQuote.conversionRemarksUpdatedAt || '')
    }, [buyerQuote, companyUsers])
    
    const handleConvProbabilityChange = (e) => {
        let value = e.target.value
        if (value >= 0 && value <= 100) {
            value = parseInt(value)
            setConversionProbability(value)
        }
    }

    const handleSubmit = async () => {
        const data = {
            conversionProbability : conversionProbability,
            conversionRemarks : conversionRemarks
        }
        const validation_res = await validateInputs(ConversionProbabilityValidationSchema, data)
        if(validation_res.error){
            setFormErrors(validation_res.error)
        }else{
            setFormErrors({})
            if (window.confirm("Are you sure to submit?")) {
                setIsLoading(true)
                const data = {
                    conversionProbability: conversionProbability,
                    conversionRemarks: conversionRemarks,
                    conversionRemarksUpdatedBy: userId,
                    conversionRemarksUpdatedAt: new Date(),
                }
                const response = await updateBuyerQuote(buyerQuote?.buyerQuoteId, data)
                setIsLoading(false)
                if (!response?.failure) {
                    updateBuyerQuoteState({ ...buyerQuote, ...data })
                    const user = companyUsers.find(user => user.userId === userId)
                    setUpdatedBy(user ? `${user.firstName} ${user.lastName}` : '')
                    setUpdatedAt(data.conversionRemarksUpdatedAt)
                    dispatch(raiseToast({ severity: "success", detail: "Conversion Probability has been added successfully.", life: 3000 }))
                    setIsEditing(false)
                } else {
                    dispatch(raiseToast({ severity: "error", summary: "Conversion Probability", detail: response?.data.error, life: 3000 }))
                }
            }
        }
    }

    const handleUpdate = async () => {
        const data = {
            conversionProbability : conversionProbability,
            conversionRemarks : conversionRemarks
        }
        const validation_res = await validateInputs(ConversionProbabilityValidationSchema, data)
        if(validation_res.error){
            setFormErrors(validation_res.error)
        }else{
            setFormErrors({})
            if (window.confirm("Are you sure to update?")) {
                setIsLoading(true)
                const data = {
                    conversionProbability: conversionProbability,
                    conversionRemarks: conversionRemarks,
                    conversionRemarksUpdatedBy: userId,
                    conversionRemarksUpdatedAt: new Date(),
                }
                const response = await updateBuyerQuote(buyerQuote?.buyerQuoteId, data)
                setIsLoading(false)
                if (!response?.failure) {
                    updateBuyerQuoteState({ ...buyerQuote, ...data })
                    const user = companyUsers.find(user => user.userId === userId)
                    setUpdatedBy(user ? `${user.firstName} ${user.lastName}` : '')
                    setUpdatedAt(data.conversionRemarksUpdatedAt)
                    dispatch(raiseToast({ severity: "success", detail: "Conversion Probability has been updated successfully.", life: 3000 }))
                    setIsEditing(false)
                } else {
                    dispatch(raiseToast({ severity: "error", summary: "Error!", detail: response?.data.error, life: 3000 }))
                }
            }
        }
    }

    const handleDiscard = () => {
        if (window.confirm("Are you sure to discard?")){
            setConversionProbability(buyerQuote.conversionProbability || '')
            setConversionRemarks(buyerQuote.conversionRemarks || '')
            setIsEditing(false)
            setFormErrors({})
        }
    }

    return (
        <div className="flex flex-column pb-2 mb-2">
            <Loading visible={isLoading} />
            <div className="flex flex-column mt-5">
                <TextInputField
                    name="conversionProbability"
                    labelText="Conversion Probability (1-100)"
                    value={conversionProbability}
                    keyfilter={"pnum"}
                    onChange={handleConvProbabilityChange}
                    disabled={!isEditing || !canEdit}
                    errorMessage={formErrors.conversionProbability}
                    required
                />
            </div>
            <div className="flex flex-column mt-3">
                <TextAreaField
                    name="conversionRemarks"
                    value={conversionRemarks}
                    onChange={(e) => setConversionRemarks(e.target.value)}
                    labelText="Conversion Remarks"
                    placeholder="Add remarks"
                    maxLength={500000}
                    rows={5}
                    disabled={!isEditing || !canEdit}
                    errorMessage={formErrors.conversionRemarks}
                    required
                />
            </div>
            <div className="flex flex-row justify-content-end mt-2 mb-2">
                {(!buyerQuote.conversionProbability && !buyerQuote.conversionRemarks) ? (
                    <span className="custom-tooltip-conversion-prob">
                        <Button className="mt-5" label="Submit" onClick={handleSubmit} disabled={!canEdit} />
                    </span>
                ) : (
                    isEditing ? (
                        <>
                            <Button className="mt-5 mr-2 p-button-warning" label="Discard" onClick={handleDiscard} />
                            <Button className="mt-5" label="Update" onClick={handleUpdate} />
                        </>
                    ) : (
                        <span className="custom-tooltip-conversion-prob">
                            <Button className="mt-5" label="Edit" onClick={() => setIsEditing(true)} disabled={!canEdit} />
                        </span>
                    )
                )}
                <Tooltip target={`.custom-tooltip-conversion-prob`} position="top" autoHide>
                    {tooltipMessage}
                </Tooltip>
            </div>

            {buyerQuote.conversionProbability && buyerQuote.conversionRemarks && (
                <>
                    <hr />
                    <div>
                        <p><b>Last Updated By:</b> {updatedBy}</p>
                        <p><b>Last Updated At:</b> {new Date(updatedAt).toLocaleString()}</p>
                    </div>
                </>
            )}
        </div>
    )
}

export default ConversionProbabilityDialog